import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import SelectedPackageSubscriptions from "./SelectedPackageSubscriptions";
import {
  DatePicker,
  ErrorMessage,
  PrimaryButton,
  RadioButton,
  Textfield,
} from "widgets";
import {
  useFetchUpdatedPrice,
  useSearchSalesAgent,
} from "store/backend/assets";
import {
  DISCOUNT_MODES,
  DISCOUNT_TYPE,
  MAXIMUM_DYNAMIC_DISCOUNT_VALUE,
  MINIMUM_DYNAMIC_DISCOUNT_VALUE,
  MIN_COUPON_CODE_LENGTH,
} from "constants/config";
import { useSearchParams } from "react-router-dom";
import { reduceToFixedDecimals } from "utils/amountCalculation";
import { formatMonetaryAmount } from "utils/formatMonetaryAmount";
import { formatL0InfoLabel } from "utils/formatL0InfoDisplay";
import dayjs from "dayjs";
import AddressForm from "components/AddressForm/AddressForm";
import Search from "widgets/Dropdown/SearchableSingleSelect";

import styles from "./OrderSummary.module.scss";

const calculateTotalMRP = (selectedPackageSubscriptions) => {
  const totalMrp = selectedPackageSubscriptions.reduce(
    (total, packageSubscription) => {
      const mrp = packageSubscription.mrp;
      return total + mrp;
    },
    0
  );
  return reduceToFixedDecimals(totalMrp);
};

const handleDynamicDiscount = (
  discount,
  setErrorMessage,
  setFieldValue,
  setDiscountValue,
  currencyCode,
) => {
  setDiscountValue(discount);
  const trimmedDiscount = discount.trim();
  const isPercentage = trimmedDiscount.includes("%");
  const numericValue = parseFloat(trimmedDiscount.replace("%", ""));
  const isValidFormat = /^\d+(\.\d{1,2})?%?$/.test(trimmedDiscount);

  if (!discount) {
    setErrorMessage("");
    setFieldValue("orderDetails.discount", null);
    return;
  }

  if (!isValidFormat) {
    setErrorMessage(
      "Enter a valid number or percentage (e.g., 1000, 10.5, or 10.25%)."
    );
    setFieldValue("orderDetails.discount", null);
    return;
  }

  if (isNaN(numericValue)) {
    setErrorMessage("Invalid numeric value.");
    setFieldValue("orderDetails.discount", null);
    return;
  }

  if (numericValue <= MINIMUM_DYNAMIC_DISCOUNT_VALUE) {
    setErrorMessage("Discount must be greater than 0.");
    setFieldValue("orderDetails.discount", null);
    return;
  }

  if (isPercentage && numericValue > 100) {
    setErrorMessage("Percentage cannot be more than 100%.");
    setFieldValue("orderDetails.discount", null);
    return;
  }

  if (!isPercentage && numericValue >= MAXIMUM_DYNAMIC_DISCOUNT_VALUE) {
    setErrorMessage(
      `Discount amount must be less than ${formatMonetaryAmount(
        MAXIMUM_DYNAMIC_DISCOUNT_VALUE,
        currencyCode,
      )}.`,
    );
    setFieldValue("orderDetails.discount", null);
    return;
  }

  setErrorMessage("");
  const dynamicDiscountPayload = {
    discount_value: numericValue,
    is_percentage: isPercentage,
    discount_type: DISCOUNT_TYPE.DYNAMIC,
  };
  setFieldValue("orderDetails.discount", dynamicDiscountPayload);
};

const handleCodeDiscount = (
  couponCode,
  setCouponCode,
  discountValue,
  setDiscountValue,
  setErrorMessage,
  setFieldValue,
  customer_id
) => {
  setCouponCode(couponCode);
  setDiscountValue(discountValue);
  const trimmedDiscount = discountValue?.trim();
  const isPercentage = trimmedDiscount?.includes("%");
  const numericValue = parseFloat(trimmedDiscount?.replace("%", ""));
  const isValidFormat = /^\d+(\.\d{1,2})?%?$/.test(trimmedDiscount);

  if (discountValue && !isValidFormat) {
    setErrorMessage(
      "Enter a valid number or percentage (e.g., 1000, 10.5, or 10.25%)."
    );
    return;
  }

  if (discountValue && isNaN(numericValue)) {
    setErrorMessage("Invalid numeric value.");
    return;
  }

  if (isPercentage && numericValue > 100) {
    setErrorMessage("Percentage cannot be more than 100%.");
    return;
  }

  setErrorMessage("");

  const couponDiscountPayload = {
    coupon_code: couponCode,
    customer_id: customer_id,
    discount_type: DISCOUNT_TYPE.COUPON_CODE,
    ...(discountValue && {
      discount_value: numericValue,
      is_percentage: isPercentage,
    }),
  };

  setFieldValue("orderDetails.discount", couponDiscountPayload);
};

const handleRemoveDiscount = (
  queryClient,
  setFieldValue,
  setIsValueApplied,
  setValue,
  mutationVariables
) => {
  setValue("");
  setIsValueApplied(false);
  setFieldValue("priceDetailsAfterDiscount", {});
  queryClient.removeQueries({ queryKey: ["discount", mutationVariables] });
};

const handleRemoveUpdatedDiscount = async (
  queryClient,
  setFieldValue,
  setIsValueApplied,
  setValue,
  refetchUpdatedPrice,
  setIsCodeApplied,
  setErrorMessage,
  handleApplyDiscount,
  discount,
  mutationVariables,
  currencyCode
) => {
  await queryClient.removeQueries({
    queryKey: ["discount", mutationVariables],
  });
  setValue("");
  setIsValueApplied(false);
  setFieldValue("priceDetailsAfterDiscount", {});

  await setFieldValue("orderDetails.discount", {
    coupon_code: discount.coupon_code,
    customer_id: discount.customer_id,
    discount_type: discount.discount_type,
  });

  await handleApplyDiscount(
    refetchUpdatedPrice,
    setFieldValue,
    setIsCodeApplied,
    setErrorMessage,
    currencyCode
  );
};

const changeDiscountMode = (
  value,
  setDiscountMode,
  setCouponCode,
  setDiscountValue,
  setIsCodeApplied,
  setErrorMessage,
  setIsDiscountValueApplied,
  setFieldValue,
  queryClient,
  mutationVariables
) => {
  setDiscountMode(value);
  setCouponCode("");
  setDiscountValue("");
  setIsCodeApplied(false);
  setErrorMessage("");
  setIsDiscountValueApplied(false);
  setFieldValue("orderDetails.discount", null);
  queryClient.removeQueries({ queryKey: ["discount", mutationVariables] });
};

const buildMutationVariables = (
  selectedPackageSubscriptions,
  discount,
  gatewayTenantId,
) => {
  const skus = selectedPackageSubscriptions.map((packageSubscription) => ({
    id: packageSubscription.packageId,
    subscriptions: [{ id: packageSubscription.id }],
  }));

  return {
    skus,
    discount,
    gateway_tenant_id: gatewayTenantId,
  };
};

const getPriceAfterDiscount = (updatedPriceDetails) => {
  const priceAfterDiscount = updatedPriceDetails?.price_after_discount;
  if (priceAfterDiscount != null) {
    return reduceToFixedDecimals(priceAfterDiscount);
  } else {
    return null;
  }
};

const formatFetchPriceErrorMessage = (message, currencyCode) => {
  if (!message) {
    return "An error occurred while processing the request.";
  }

  const percentageMatch = message.match(
    /(?:maximum|max)\s+allowed\s+discount(?:\s+amount)?\s+of\s+(\d+(?:\.\d+)?)\s*(?:%|percentage)\b/i,
  );
  if (percentageMatch) {
    return `Discount value exceeds maximum allowed discount of ${parseFloat(
      percentageMatch[1],
    )}%`;
  }

  const amountMatch = message.match(
    /(?:maximum|max)\s+allowed\s+discount(?:\s+amount)?\s+of\s+(\d+(?:\.\d+)?)\s*$/i,
  );
  if (amountMatch) {
    return `Discount value exceeds maximum allowed discount of ${formatMonetaryAmount(
      Number(amountMatch[1]),
      currencyCode,
    )}`;
  }

  return message;
};

const handleApplyDiscount = (
  refetchUpdatedPrice,
  setFieldValue,
  setIsValueApplied,
  setErrorMessage,
  currencyCode
) => {
  refetchUpdatedPrice()
    .then((result) => {
      if (result.error) {
        setErrorMessage(
          formatFetchPriceErrorMessage(
            result?.error?.message,
            currencyCode,
          ),
        );
        return;
      }
      setFieldValue("priceDetailsAfterDiscount", {
        listedOrderAmount: result.data.total_order_amount,
        orderAmount: result.data.price_after_discount,
        package_details: result.data.package_details || [],
      });
      setIsValueApplied(true);
    })
    .catch(() => {
      setErrorMessage("An error occurred while processing the request.");
    });
};

const handleUseSameAddress = (useSameAddress, setFieldValue, orderDetails) => {
  const newUseSameAddress = !useSameAddress;
  setFieldValue("useSameAddress", newUseSameAddress);

  if (newUseSameAddress) {
    setFieldValue("orderDetails", {
      ...orderDetails,
      shippingAddress: orderDetails.billingAddress,
    });
  } else {
    setFieldValue("orderDetails", {
      ...orderDetails,
      shippingAddress: {},
    });
  }
};

const getAgents = (agentsData, isAgentsLoading) => {
  if (isAgentsLoading || !agentsData) {
    return [];
  }
  return agentsData.map((agent) => ({
    label: agent.label,
    value: agent.key,
    raw: agent,
  }));
};

const OrderSummary = (props) => {
  const {
    orderDetails,
    selectedVerticalId,
    gatewayTenantId,
    countryName,
    isdCode,
    countryCode,
    onIsdCodeChange,
    selectedPackageSubscriptions,
    setFieldValue,
    useSameAddress,
    isOrderTransfer,
    errors,
    handleBlur,
    validateForm,
    currencyCode,
  } = props;

  const { discount, serviceStartDate, attributedTo, attributedToName } =
    orderDetails;
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const customer_id = searchParams.get("uamId");
  const [discountMode, setDiscountMode] = useState(DISCOUNT_MODES[0].value);
  const [couponCode, setCouponCode] = useState("");
  const [discountValue, setDiscountValue] = useState("");
  const [isCodeApplied, setIsCodeApplied] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDiscountValueApplied, setIsDiscountValueApplied] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const totalMRP = calculateTotalMRP(selectedPackageSubscriptions);
  const discoutMutationVariables = buildMutationVariables(
    selectedPackageSubscriptions,
    discount,
    gatewayTenantId,
  );
  const {
    isLoading: isUpdatedPriceLoading,
    data: updatedPriceDetails,
    refetch: refetchUpdatedPrice,
  } = useFetchUpdatedPrice(discoutMutationVariables);

  const l0ProvidedByOrder = Boolean(orderDetails.l0ProvidedByOrder);
  const showL0ReadOnly = isOrderTransfer && l0ProvidedByOrder;

  const { isLoading: isAgentsLoading, data: agentsData } = useSearchSalesAgent(
    searchTerm,
    !isOrderTransfer || !l0ProvidedByOrder,
  );

  return (
    <div className={styles.orderSummaryContainer}>
      <AddressForm
        billingAddress={orderDetails.billingAddress}
        shippingAddress={orderDetails.shippingAddress}
        handleChangeBillingAddress={(fieldName, fieldValue) => {
          setFieldValue(
            `orderDetails.billingAddress.${fieldName}`,
            fieldValue,
            true
          );
          if (fieldName === "stateId") {
            setTimeout(() => {
              validateForm();
            }, 0);
          }
        }}
        handleChangeShippingAddress={(fieldName, fieldValue) => {
          setFieldValue(
            `orderDetails.shippingAddress.${fieldName}`,
            fieldValue,
            true
          );
          if (fieldName === "stateId") {
            setTimeout(() => {
              validateForm();
            }, 0);
          }
        }}
        useSameAddress={useSameAddress}
        handleUseSameAddress={() =>
          handleUseSameAddress(useSameAddress, setFieldValue, orderDetails)
        }
        hasHardware={orderDetails.hasHardware}
        errors={errors}
        handleBlur={handleBlur}
        isOrderTransfer={isOrderTransfer}
        validateForm={validateForm}
        selectedVerticalId={selectedVerticalId}
        countryName={countryName}
        isdCode={isdCode}
        countryCode={countryCode}
        onIsdCodeChange={onIsdCodeChange}
        gatewayTenantId={gatewayTenantId}
      />
      <div className={styles.flexContainer}>
        <div className={styles.startServiceDate}>
          <div className={styles.startServiceDateTitle}>
            Start Service Date<span className={styles.required}>*</span>
          </div>
          <DatePicker
            value={serviceStartDate}
            onChange={(date) =>
              setFieldValue("orderDetails.serviceStartDate", date)
            }
            minDate={dayjs()}
          />
        </div>
        <div className={styles.attributedTo}>
          {showL0ReadOnly ? (
            <>
              <div className={styles.attributedToTitle}>L0 Sales Person</div>
              <div className={styles.attributedToReadonlyField}>
                <div className={styles.attributedToFixedValue}>
                  {(orderDetails.l0Info &&
                    formatL0InfoLabel(orderDetails.l0Info)) ||
                    (attributedToName
                      ? attributedToName
                      : attributedTo != null && attributedTo !== ""
                        ? String(attributedTo)
                        : "—")}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={styles.attributedToTitle}>
                L0 Sales Person<span className={styles.required}>*</span>
              </div>
              <Search
                id="attributedTo"
                name="attributedTo"
                placeholder="Search agent"
                options={agentsData || []}
                value={
                  (agentsData || []).find(
                    (opt) => opt.value === orderDetails.attributedTo,
                  ) ||
                  (orderDetails.attributedToName
                    ? {
                        label: orderDetails.attributedToName,
                        value: orderDetails.attributedTo,
                      }
                    : null)
                }
                getOptionLabel={(option) => option?.label ?? ""}
                isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
                loading={isAgentsLoading}
                handleOnChangeSearch={(text) => {
                  setSearchTerm(text);
                  if (text === "") {
                    setFieldValue("orderDetails.attributedTo", "");
                    setFieldValue("orderDetails.attributedToName", "");
                  }
                }}
                handleOnSelectSearchItem={(selected) => {
                  if (!selected) {
                    setFieldValue("orderDetails.attributedTo", "");
                    setFieldValue("orderDetails.attributedToName", "");
                    return;
                  }
                  setFieldValue("orderDetails.attributedTo", selected.value);
                  setFieldValue("orderDetails.attributedToName", selected.label);
                  setSearchTerm("");
                }}
                onClose={() => {}}
              />
            </>
          )}
        </div>
        <div className={styles.packageContainer}>
          <SelectedPackageSubscriptions
            selectedPackageSubscriptions={selectedPackageSubscriptions}
            disableDeleteIcon={true}
            currencyCode={currencyCode}
          />
        </div>
        <div className={styles.discountContainer}>
          <div className={styles.discountField}>
            <span className={styles.discountLabel}>Total Cart Value</span>
            <span className={styles.amountValues}>
              {formatMonetaryAmount(totalMRP, currencyCode)}
            </span>
          </div>
          <span className={styles.discountLabel}>Mode of Discount</span>
          <div className={styles.discountModes}>
            <RadioButton
              value={discountMode}
              options={DISCOUNT_MODES}
              onChange={(event) =>
                changeDiscountMode(
                  event.target.value,
                  setDiscountMode,
                  setCouponCode,
                  setDiscountValue,
                  setIsCodeApplied,
                  setErrorMessage,
                  setIsDiscountValueApplied,
                  setFieldValue,
                  queryClient,
                  discoutMutationVariables
                )
              }
              row={true}
            />
          </div>
          {discountMode === "dynamic" && (
            <div className={styles.discountTextFieldContainer}>
              <div className={styles.discountTextField}>
                <Textfield
                  label="Enter amount or %"
                  value={discountValue}
                  handleOnChange={(event) => {
                    const dynamicValue = event.target.value;
                    handleDynamicDiscount(
                      dynamicValue,
                      setErrorMessage,
                      setFieldValue,
                      setDiscountValue,
                      currencyCode,
                    );
                  }}
                  disabled={isDiscountValueApplied}
                />
                {!isDiscountValueApplied ? (
                  !isUpdatedPriceLoading ? (
                    <PrimaryButton
                      onClick={() =>
                        handleApplyDiscount(
                          refetchUpdatedPrice,
                          setFieldValue,
                          setIsDiscountValueApplied,
                          setErrorMessage,
                          currencyCode
                        )
                      }
                      disabled={!discountValue || errorMessage}
                    >
                      Apply
                    </PrimaryButton>
                  ) : (
                    <PrimaryButton disabled={true}>Applying...</PrimaryButton>
                  )
                ) : (
                  <PrimaryButton
                    onClick={() =>
                      handleRemoveDiscount(
                        queryClient,
                        setFieldValue,
                        setIsDiscountValueApplied,
                        setDiscountValue,
                        discoutMutationVariables
                      )
                    }
                  >
                    Remove
                  </PrimaryButton>
                )}
              </div>
            </div>
          )}
          {discountMode === "code" && (
            <div className={styles.discountTextFieldContainer}>
              <div className={styles.discountTextField}>
                <Textfield
                  label="Enter code"
                  value={couponCode}
                  handleOnChange={(event) => {
                    const code = event.target.value;
                    handleCodeDiscount(
                      code,
                      setCouponCode,
                      discountValue,
                      setDiscountValue,
                      setErrorMessage,
                      setFieldValue,
                      customer_id
                    );
                  }}
                  disabled={isCodeApplied}
                />
                {!isCodeApplied ? (
                  !isUpdatedPriceLoading ? (
                    <PrimaryButton
                      onClick={() =>
                        handleApplyDiscount(
                          refetchUpdatedPrice,
                          setFieldValue,
                          setIsCodeApplied,
                          setErrorMessage,
                          currencyCode
                        )
                      }
                      disabled={
                        !couponCode ||
                        couponCode.length < MIN_COUPON_CODE_LENGTH ||
                        errorMessage
                      }
                    >
                      Apply
                    </PrimaryButton>
                  ) : (
                    <PrimaryButton disabled={true}>Applying...</PrimaryButton>
                  )
                ) : (
                  <PrimaryButton
                    onClick={() => {
                      handleRemoveDiscount(
                        queryClient,
                        setFieldValue,
                        setIsCodeApplied,
                        setCouponCode,
                        discoutMutationVariables
                      );
                      handleRemoveDiscount(
                        queryClient,
                        setFieldValue,
                        setIsDiscountValueApplied,
                        setDiscountValue,
                        discoutMutationVariables
                      );
                    }}
                  >
                    Remove
                  </PrimaryButton>
                )}
              </div>
              {isCodeApplied && (
                <div className={styles.discountTextField}>
                  <Textfield
                    label="Enter amount or %"
                    value={discountValue}
                    handleOnChange={(event) => {
                      const discount = event.target.value;
                      handleCodeDiscount(
                        couponCode,
                        setCouponCode,
                        discount,
                        setDiscountValue,
                        setErrorMessage,
                        setFieldValue,
                        customer_id
                      );
                    }}
                    disabled={isDiscountValueApplied}
                  />
                  {!isDiscountValueApplied ? (
                    !isUpdatedPriceLoading ? (
                      <PrimaryButton
                        onClick={() =>
                          handleApplyDiscount(
                            refetchUpdatedPrice,
                            setFieldValue,
                            setIsDiscountValueApplied,
                            setErrorMessage,
                            currencyCode
                          )
                        }
                        disabled={!discountValue || errorMessage}
                      >
                        Apply
                      </PrimaryButton>
                    ) : (
                      <PrimaryButton disabled={true}>Applying...</PrimaryButton>
                    )
                  ) : !isUpdatedPriceLoading ? (
                    <PrimaryButton
                      onClick={() => {
                        handleRemoveUpdatedDiscount(
                          queryClient,
                          setFieldValue,
                          setIsDiscountValueApplied,
                          setDiscountValue,
                          refetchUpdatedPrice,
                          setIsCodeApplied,
                          setErrorMessage,
                          handleApplyDiscount,
                          discount,
                          discoutMutationVariables,
                          currencyCode
                        );
                      }}
                    >
                      Remove
                    </PrimaryButton>
                  ) : (
                    <PrimaryButton disabled={true}>Removing...</PrimaryButton>
                  )}
                </div>
              )}
            </div>
          )}
          {!errorMessage && updatedPriceDetails && (
            <span className={styles.successMessage}>
              {" "}
              Coupon Applied Successfully. Max Discount applicable is{" "}
              {formatMonetaryAmount(
                Math.floor(
                  (updatedPriceDetails.total_order_amount -
                    updatedPriceDetails.price_after_discount) *
                    100,
                ) / 100,
                currencyCode,
              )}
            </span>
          )}
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          <div className={styles.grandTotalRow}>
            <span className={styles.discountLabel}>Grand Total</span>
            {isUpdatedPriceLoading ? (
              <span className={styles.amountValues}>Loading...</span>
            ) : (
              <span className={styles.amountValues}>
                {formatMonetaryAmount(
                  getPriceAfterDiscount(updatedPriceDetails) ?? totalMRP,
                  currencyCode,
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
